import { useState, useEffect } from 'react'
import { useCSVStore, useCsvRuntimeStore } from '../../../model/csvStore'
import { useObjectTypeStore } from '../../../../../map-editor-shared/store/objectTypeStore'
import { useProjectStore } from '../../../../../map-editor-shared/store/projectStore'
import { useFloorStore } from '../../../../../map-editor-shared/store/floorStore'
import { log, LogCategory } from '../../../../../map-editor-shared/lib/logger'
import { FileInfo } from '../../../../../models/FileStorage'
import { LayerInfo } from '../../../../../services/DxfParserService'
import type { ViewMode } from '../types'

export function useCSVUploadState(mapId?: string) {
  const uploadState = useCSVStore(s => s.uploadState)
  const rawData = useCSVStore(s => s.rawData)
  const parsedData = useCSVStore(s => s.parsedData)
  const setFile = useCSVStore(s => s.setFile)
  const setUploadState = useCSVStore(s => s.setUploadState)
  const clearFile = useCSVStore(s => s.clearFile)
  const parseFile = useCSVStore(s => s.parseFile)
  const types = useObjectTypeStore(state => state.types)
  const isLoadingTypes = useObjectTypeStore(state => state.isLoading)
  const currentProjectId = useObjectTypeStore(state => state.currentProjectId)
  const siteProjectId = useObjectTypeStore(state => state.siteProjectId)
  const currentLot = useProjectStore(state => state.currentLot)
  const hasObjectTypes = types.length > 0

  // CRITICAL: currentFloor from FloorStore (for layer mapping modal)
  const storeCurrentFloor = useFloorStore(state => state.currentFloor)

  const [viewMode, setViewMode] = useState<ViewMode>('idle')
  // CSV 업로드 선택 화면 제거로 항상 DXF 고정 — 레이어 선택 모달을 띄우기 위해
  // 내부적으로 거치는 'source-select' 뷰가 null 로 인해 file-type-select 로
  // 되돌아가지 않도록 null 대신 'dxf' 를 기본값/리셋값으로 둔다.
  const [selectedFileType, setSelectedFileType] = useState<import('../../FileTypeSelector').FileType | null>('dxf')
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [downloadMessage, setDownloadMessage] = useState<string>('파일 다운로드 중...')
  const [showLayerModal, setShowLayerModal] = useState(false)
  const [analyzedLayers, setAnalyzedLayers] = useState<LayerInfo[]>([])
  const [selectedDxfFile, setSelectedDxfFile] = useState<FileInfo | null>(null)
  const [prevProjectId, setPrevProjectId] = useState<number | null>(null)
  const [prevLotId, setPrevLotId] = useState<string | null>(null)
  const [prevMapId, setPrevMapId] = useState<string | undefined>(undefined)

  // showLayerModal 을 전역에 그대로 반영 — EditorGuide(가이드 맵 생성)가 프롭 없이
  // "레이어 선택 모달이 떠 있다"를 읽어 풀페이지 카드를 비켜주는 데 쓴다.
  useEffect(() => {
    useCsvRuntimeStore.getState().setDxfLayerModalOpen(showLayerModal)
  }, [showLayerModal])

  // Reset CSV state when project, Lot, or Map changes
  useEffect(() => {
    // mapId changed (entering new map or switching maps)
    if (mapId !== prevMapId && prevMapId !== undefined) {
      log.info(LogCategory.CSV, 'Map changed, resetting all CSV state', {
        prevMapId,
        currentMapId: mapId
      })
      clearFile()
      setViewMode('idle')
      setDownloadError(null)
      setDownloadMessage('파일 다운로드 중...')
      setSelectedFileType('dxf')
      setShowLayerModal(false)
      setAnalyzedLayers([])
      setSelectedDxfFile(null)
      useCsvRuntimeStore.getState().setDxfListReady(false)
    }
    setPrevMapId(mapId)

    // Project ID changed
    if (currentProjectId !== prevProjectId && prevProjectId !== null) {
      log.info(LogCategory.CSV, 'Project changed, clearing CSV state', {
        prevProjectId,
        currentProjectId
      })
      clearFile()
      setViewMode('idle')
      setDownloadError(null)
      setDownloadMessage('파일 다운로드 중...')
      setSelectedFileType('dxf')
      useCsvRuntimeStore.getState().setDxfListReady(false)
    }
    setPrevProjectId(currentProjectId)

    // Lot changed
    if (currentLot !== prevLotId && prevLotId !== null) {
      log.info(LogCategory.CSV, 'Lot changed, clearing CSV state', {
        prevLotId,
        currentLotId: currentLot
      })
      clearFile()
      setViewMode('idle')
      setDownloadError(null)
      setDownloadMessage('파일 다운로드 중...')
      setSelectedFileType('dxf')
      useCsvRuntimeStore.getState().setDxfListReady(false)
    }
    setPrevLotId(currentLot)
  }, [mapId, currentProjectId, currentLot, prevMapId, prevProjectId, prevLotId, clearFile])

  const handleClear = () => {
    clearFile()
    setViewMode('idle')
    setDownloadError(null)
    setDownloadMessage('파일 다운로드 중...')
  }

  const handleRetry = () => {
    setUploadState({ status: 'idle' })
    setViewMode('idle')
    setDownloadError(null)
    setDownloadMessage('파일 다운로드 중...')
  }

  return {
    // Store state
    uploadState, rawData, parsedData, setFile, setUploadState, clearFile, parseFile,
    // Derived
    hasObjectTypes, isLoadingTypes, currentProjectId, siteProjectId, currentLot, storeCurrentFloor,
    // Local state
    viewMode, setViewMode,
    selectedFileType, setSelectedFileType,
    downloadError, setDownloadError,
    downloadMessage, setDownloadMessage,
    showLayerModal, setShowLayerModal,
    analyzedLayers, setAnalyzedLayers,
    selectedDxfFile, setSelectedDxfFile,
    // Handlers
    handleClear, handleRetry,
  }
}
